import { SshMessageReader } from './message-reader.js';
import { SshMessageWriter } from './message-writer.js';
import {
	SSH_MSG_SERVICE_ACCEPT,
	SSH_MSG_USERAUTH_REQUEST,
	SSH_MSG_USERAUTH_FAILURE,
	SSH_MSG_USERAUTH_SUCCESS,
	SSH_MSG_USERAUTH_BANNER,
	SSH_MSG_USERAUTH_PK_OK,
} from './constants.js';
import type { SshTransportProtocol } from './transport-protocol.js';
import { parsePrivateKey, signWithKey, type SshPrivateKey } from '../crypto/key-parser.js';
import { Emitter, type Logger, noopLogger } from '@ts-ssh2/common';

/**
 * Authentication credentials.
 */
export type AuthCredentials = {
	username: string;
	password?: string;
	privateKey?: string;
	passphrase?: string;
};

/**
 * SSH authentication protocol (RFC 4252).
 *
 * Flow:
 *   1. Request "ssh-userauth" service
 *   2. Probe with "none" method to discover allowed methods
 *   3. Try "publickey" if a private key is provided
 *   4. Try "password" if a password is provided
 *   5. Throw if all methods exhausted
 */
export class SshAuthProtocol {
	private transport: SshTransportProtocol;
	private credentials: AuthCredentials;
	private logger: Logger;

	/** Emits banner messages from the server. */
	readonly onBanner = new Emitter<string>();

	constructor(transport: SshTransportProtocol, credentials: AuthCredentials, logger?: Logger) {
		this.transport = transport;
		this.credentials = credentials;
		this.logger = logger ?? noopLogger;
	}

	/**
	 * Perform authentication.
	 * Resolves when authentication succeeds, rejects on failure.
	 */
	async authenticate(): Promise<void> {
		// request the ssh-userauth service
		this.logger.debug('requesting ssh-userauth service');
		await this.requestService();

		// probe with "none" to get the list of allowed methods
		this.logger.debug('probing with "none" auth method');
		const allowedMethods = await this.tryNone();
		if (allowedMethods === null) {
			// "none" succeeded (unlikely but possible)
			this.logger.debug('auth succeeded with "none" method');
			return;
		}

		this.logger.debug(`server allows auth methods: [${allowedMethods.join(', ')}]`);

		// try publickey if we have a key and the server supports it
		if (this.credentials.privateKey && allowedMethods.includes('publickey')) {
			this.logger.debug('trying publickey auth');
			const key = await parsePrivateKey(this.credentials.privateKey, this.credentials.passphrase);
			const result = await this.tryPublicKey(key);
			if (result === null) {
				this.logger.debug('auth succeeded with publickey');
				return;
			}
		}

		// try password if we have one and the server supports it
		if (this.credentials.password && allowedMethods.includes('password')) {
			this.logger.debug('trying password auth');
			const result = await this.tryPassword();
			if (result === null) {
				this.logger.debug('auth succeeded with password');
				return;
			}
		}

		throw new Error(
			`Authentication failed: no more methods to try. Server allows: [${allowedMethods.join(', ')}]`,
		);
	}

	/**
	 * Request the "ssh-userauth" service and wait for acceptance.
	 */
	private async requestService(): Promise<void> {
		await this.transport.requestService('ssh-userauth');
		const response = await this.waitForMessage();

		const reader = new SshMessageReader(response);
		const msgType = reader.readByte();

		if (msgType !== SSH_MSG_SERVICE_ACCEPT) {
			throw new Error(`Expected SERVICE_ACCEPT, got message type ${msgType}`);
		}

		const serviceName = reader.readStringAsText();
		if (serviceName !== 'ssh-userauth') {
			throw new Error(`Server accepted unexpected service: ${serviceName}`);
		}
	}

	/**
	 * Try "none" authentication. This always "fails" but the failure
	 * response tells us which methods the server supports.
	 *
	 * @returns The list of allowed methods, or null if "none" succeeded.
	 */
	private async tryNone(): Promise<string[] | null> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_USERAUTH_REQUEST);
		w.writeString(this.credentials.username);
		w.writeString('ssh-connection'); // service name
		w.writeString('none'); // method

		await this.transport.send(w.toUint8Array());
		return this.readAuthResponse();
	}

	/**
	 * Try publickey authentication (two-step: probe then sign).
	 *
	 * Step 1: Send a probe with has_signature=false.
	 *   - If server replies PK_OK, proceed to step 2.
	 *   - If server replies FAILURE, this key is not accepted.
	 *
	 * Step 2: Send the actual signed request.
	 *   - On SUCCESS, we're in.
	 *   - On FAILURE, the key was not sufficient.
	 *
	 * @returns The list of remaining methods on failure, or null on success.
	 */
	private async tryPublicKey(key: SshPrivateKey): Promise<string[] | null> {
		// step 1: probe
		const probe = new SshMessageWriter();
		probe.writeByte(SSH_MSG_USERAUTH_REQUEST);
		probe.writeString(this.credentials.username);
		probe.writeString('ssh-connection');
		probe.writeString('publickey');
		probe.writeBoolean(false); // has_signature = false
		probe.writeString(key.signingAlgorithm);
		probe.writeString(key.publicKeyBlob);

		await this.transport.send(probe.toUint8Array());
		const probeResponse = await this.readAuthResponseRaw();

		if (probeResponse.type === 'failure') {
			return probeResponse.methods;
		}

		if (probeResponse.type === 'success') {
			return null; // unlikely but handle it
		}

		if (probeResponse.type !== 'pk_ok') {
			throw new Error('Unexpected auth response during publickey probe');
		}

		// step 2: sign and send
		// the data to sign is:
		//   string    session_id
		//   byte      SSH_MSG_USERAUTH_REQUEST
		//   string    username
		//   string    "ssh-connection"
		//   string    "publickey"
		//   boolean   TRUE
		//   string    algorithm
		//   string    public_key_blob
		const sessionId = this.transport.getSessionId();

		const signData = new SshMessageWriter();
		signData.writeString(sessionId);
		signData.writeByte(SSH_MSG_USERAUTH_REQUEST);
		signData.writeString(this.credentials.username);
		signData.writeString('ssh-connection');
		signData.writeString('publickey');
		signData.writeBoolean(true);
		signData.writeString(key.signingAlgorithm);
		signData.writeString(key.publicKeyBlob);

		const signature = await signWithKey(key, signData.toUint8Array());

		const signed = new SshMessageWriter();
		signed.writeByte(SSH_MSG_USERAUTH_REQUEST);
		signed.writeString(this.credentials.username);
		signed.writeString('ssh-connection');
		signed.writeString('publickey');
		signed.writeBoolean(true); // has_signature = true
		signed.writeString(key.signingAlgorithm);
		signed.writeString(key.publicKeyBlob);
		signed.writeString(signature);

		await this.transport.send(signed.toUint8Array());
		return this.readAuthResponse();
	}

	/**
	 * Try password authentication.
	 *
	 * @returns The list of remaining methods on failure, or null on success.
	 */
	private async tryPassword(): Promise<string[] | null> {
		const w = new SshMessageWriter();
		w.writeByte(SSH_MSG_USERAUTH_REQUEST);
		w.writeString(this.credentials.username);
		w.writeString('ssh-connection');
		w.writeString('password');
		w.writeBoolean(false); // not a password change request
		w.writeString(this.credentials.password!);

		await this.transport.send(w.toUint8Array());
		return this.readAuthResponse();
	}

	// ── message reading helpers ────────────────────────────────────────

	/**
	 * Read auth response, handling banners.
	 * @returns The list of allowed methods on failure, or null on success.
	 */
	private async readAuthResponse(): Promise<string[] | null> {
		const response = await this.readAuthResponseRaw();
		if (response.type === 'success') return null;
		if (response.type === 'failure') return response.methods;
		throw new Error(`Unexpected auth response: ${response.type}`);
	}

	private async readAuthResponseRaw(): Promise<AuthResponse> {
		while (true) {
			const msg = await this.waitForMessage();
			const msgType = msg[0]!;

			if (msgType === SSH_MSG_USERAUTH_BANNER) {
				const reader = new SshMessageReader(msg);
				reader.readByte(); // msg type
				const banner = reader.readStringAsText();
				this.onBanner.emit(banner);
				continue;
			}

			if (msgType === SSH_MSG_USERAUTH_SUCCESS) {
				return { type: 'success' };
			}

			if (msgType === SSH_MSG_USERAUTH_FAILURE) {
				const reader = new SshMessageReader(msg);
				reader.readByte(); // msg type
				const methods = reader.readNameList();
				const partialSuccess = reader.readBoolean();
				return { type: 'failure', methods, partialSuccess };
			}

			if (msgType === SSH_MSG_USERAUTH_PK_OK) {
				return { type: 'pk_ok' };
			}

			throw new Error(`Unexpected message type during auth: ${msgType}`);
		}
	}

	/**
	 * Wait for the next message from the transport.
	 * Uses a one-shot listener on the transport's onMessage emitter.
	 */
	private waitForMessage(): Promise<Uint8Array> {
		return new Promise<Uint8Array>((resolve) => {
			const sub = this.transport.onMessage.subscribe((msg) => {
				sub.dispose();
				resolve(msg);
			});
		});
	}

	dispose(): void {
		this.onBanner.dispose();
	}
}

type AuthResponse =
	| { type: 'success' }
	| { type: 'failure'; methods: string[]; partialSuccess: boolean }
	| { type: 'pk_ok' };
